import React, { useEffect, useRef, useState } from "react";
import api, { fileUrl } from "../../../lib/api";
import { AdminButton, AdminInput, AdminTextarea, AdminCard } from "../AdminUI";
import { Save, Loader2, Plus, Trash2, Upload, ImageOff, ChevronUp, ChevronDown, Eye, EyeOff, Pencil, Check, X, Megaphone } from "lucide-react";
import { toast } from "sonner";

const emptyPromo = { title: "", text: "", image_url: "", active: true, sort_order: 0 };

const PromotionsTab = () => {
    const [promos, setPromos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [draft, setDraft] = useState(emptyPromo);
    const [editing, setEditing] = useState(null);
    const [uploadingFor, setUploadingFor] = useState(null);
    const draftFileRef = useRef(null);

    const reload = async () => {
        const r = await api.get("/promotions");
        setPromos(r.data);
        setLoading(false);
    };
    useEffect(() => { reload(); }, []);

    const uploadImage = async (file) => {
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        return up.data.url;
    };

    const onDraftFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setUploadingFor("new");
        try {
            const url = await uploadImage(f);
            setDraft({ ...draft, image_url: url });
            toast.success("Bild hochgeladen");
        } catch { toast.error("Upload fehlgeschlagen"); }
        finally { setUploadingFor(null); if (draftFileRef.current) draftFileRef.current.value = ""; }
    };

    const onEditFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f || !editing) return;
        setUploadingFor(editing.id);
        try {
            const url = await uploadImage(f);
            setEditing({ ...editing, image_url: url });
            toast.success("Bild hochgeladen");
        } catch { toast.error("Upload fehlgeschlagen"); }
        finally { setUploadingFor(null); }
    };

    const addPromo = async () => {
        if (!draft.title.trim()) return toast.error("Titel ist erforderlich");
        await api.post("/promotions", { ...draft, sort_order: promos.length });
        setCreating(false);
        setDraft(emptyPromo);
        await reload();
        toast.success("Aktion erstellt");
    };

    const savePromo = async (p) => {
        if (!p.title.trim()) return toast.error("Titel ist erforderlich");
        await api.put(`/promotions/${p.id}`, p);
        setEditing(null);
        await reload();
        toast.success("Aktion aktualisiert");
    };

    const toggleActive = async (p) => {
        await api.put(`/promotions/${p.id}`, { ...p, active: !p.active });
        await reload();
    };

    const removePromo = async (id) => {
        if (!window.confirm("Aktion löschen?")) return;
        await api.delete(`/promotions/${id}`);
        await reload();
        toast.success("Aktion gelöscht");
    };

    const movePromo = async (id, dir) => {
        const ordered = [...promos];
        const idx = ordered.findIndex((p) => p.id === id);
        const t = idx + dir;
        if (t < 0 || t >= ordered.length) return;
        [ordered[idx], ordered[t]] = [ordered[t], ordered[idx]];
        setPromos(ordered);
        try {
            await api.post("/promotions/reorder", { ids: ordered.map((p) => p.id) });
        } catch { toast.error("Fehler beim Sortieren"); reload(); }
    };

    if (loading) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-garden-green" /></div>;

    const renderImage = (url, size = "w-24 h-24") => url ? (
        <img src={fileUrl(url)} alt="" className={`${size} rounded-xl object-cover flex-shrink-0 border border-cream-dark`} />
    ) : (
        <div className={`${size} rounded-xl bg-cream-dark/50 grid place-items-center flex-shrink-0 border border-dashed border-cream-dark`}>
            <ImageOff className="w-6 h-6 text-deep-green/30" />
        </div>
    );

    return (
        <div className="space-y-6 max-w-4xl" data-testid="promotions-tab">
            {!creating ? (
                <AdminButton onClick={() => { setCreating(true); setDraft(emptyPromo); }} data-testid="new-promo-open">
                    <Plus className="w-4 h-4" /> Neue Aktion anlegen
                </AdminButton>
            ) : (
                <AdminCard className="space-y-3 border-garden-green/30">
                    <h3 className="font-serif text-xl text-deep-green">Neue Aktion</h3>
                    <div className="flex gap-3 items-start">
                        {renderImage(draft.image_url)}
                        <div className="flex-1 space-y-2">
                            <AdminInput placeholder="Titel (z.B. Sommer-Spezial)" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} data-testid="new-promo-title" />
                            <AdminTextarea rows={3} placeholder="Text der Aktion" value={draft.text} onChange={(e) => setDraft({ ...draft, text: e.target.value })} data-testid="new-promo-text" />
                        </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <label className="cursor-pointer">
                            <input ref={draftFileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={onDraftFile} />
                            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-cream-dark hover:bg-cream-dark/80 text-deep-green">
                                {uploadingFor === "new" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                                {draft.image_url ? "Bild ändern" : "Bild hinzufügen"}
                            </span>
                        </label>
                        {draft.image_url && (
                            <AdminButton variant="ghost" onClick={() => setDraft({ ...draft, image_url: "" })}>
                                <ImageOff className="w-4 h-4" /> Bild entfernen
                            </AdminButton>
                        )}
                        <label className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm bg-cream-dark/60 cursor-pointer">
                            <input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} />
                            Sichtbar auf Webseite
                        </label>
                        <AdminButton onClick={addPromo} data-testid="new-promo-add"><Save className="w-4 h-4" /> Anlegen</AdminButton>
                        <AdminButton variant="ghost" onClick={() => setCreating(false)}><X className="w-4 h-4" /> Abbrechen</AdminButton>
                    </div>
                </AdminCard>
            )}

            {promos.length === 0 ? (
                <div className="bg-cream-dark/40 rounded-2xl p-10 text-center text-deep-green/60">
                    <Megaphone className="w-8 h-8 mx-auto mb-2 text-leaf-green" />
                    Noch keine Aktionen angelegt.
                </div>
            ) : (
                promos.map((p, idx) => (
                    <AdminCard key={p.id} className={`${!p.active ? "opacity-60" : ""}`} data-testid={`promo-${p.id}`}>
                        {editing?.id === p.id ? (
                            <div className="space-y-3">
                                <div className="flex gap-3 items-start">
                                    {renderImage(editing.image_url)}
                                    <div className="flex-1 space-y-2">
                                        <AdminInput value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
                                        <AdminTextarea rows={3} value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} />
                                    </div>
                                </div>
                                <div className="flex gap-2 flex-wrap">
                                    <label className="cursor-pointer">
                                        <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={onEditFile} />
                                        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-cream-dark hover:bg-cream-dark/80 text-deep-green">
                                            {uploadingFor === editing.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                                            {editing.image_url ? "Bild ändern" : "Bild hinzufügen"}
                                        </span>
                                    </label>
                                    {editing.image_url && (
                                        <AdminButton variant="ghost" onClick={() => setEditing({ ...editing, image_url: "" })}>
                                            <ImageOff className="w-4 h-4" /> Bild entfernen
                                        </AdminButton>
                                    )}
                                    <AdminButton onClick={() => savePromo(editing)}><Check className="w-4 h-4" /> Speichern</AdminButton>
                                    <AdminButton variant="ghost" onClick={() => setEditing(null)}><X className="w-4 h-4" /> Abbrechen</AdminButton>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-start gap-3">
                                <div className="flex flex-col">
                                    <button onClick={() => movePromo(p.id, -1)} disabled={idx === 0} className="p-1 rounded hover:bg-cream-dark disabled:opacity-30" title="Nach oben" data-testid={`promo-up-${p.id}`}><ChevronUp className="w-4 h-4" /></button>
                                    <button onClick={() => movePromo(p.id, 1)} disabled={idx === promos.length - 1} className="p-1 rounded hover:bg-cream-dark disabled:opacity-30" title="Nach unten" data-testid={`promo-down-${p.id}`}><ChevronDown className="w-4 h-4" /></button>
                                </div>
                                {renderImage(p.image_url)}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <h4 className="font-serif text-lg text-deep-green">{p.title}</h4>
                                        {!p.active && <span className="text-xs px-2 py-0.5 rounded-full bg-cream-dark text-deep-green/70">versteckt</span>}
                                    </div>
                                    {p.text && <p className="text-sm text-deep-green/75 whitespace-pre-line">{p.text}</p>}
                                </div>
                                <div className="flex gap-1 flex-shrink-0">
                                    <AdminButton variant="ghost" onClick={() => toggleActive(p)} title={p.active ? "Verstecken" : "Einblenden"} data-testid={`promo-toggle-${p.id}`}>
                                        {p.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                    </AdminButton>
                                    <AdminButton variant="ghost" onClick={() => setEditing(p)} data-testid={`promo-edit-${p.id}`}><Pencil className="w-4 h-4" /></AdminButton>
                                    <AdminButton variant="danger" onClick={() => removePromo(p.id)} data-testid={`promo-delete-${p.id}`}><Trash2 className="w-4 h-4" /></AdminButton>
                                </div>
                            </div>
                        )}
                    </AdminCard>
                ))
            )}
        </div>
    );
};

export default PromotionsTab;
